import { useState, useEffect } from 'react';
import { IcoClose, IcoTrash } from './common/Icons.jsx';
import { nombreDe } from '../store/cartStore.js';
import { useConfirmacion } from './ConfirmSheet.jsx';
import styles from './MoveTabSheet.module.css';

/**
 * Un comensal de la mesa: cómo se llama y si sigue sentado.
 *
 * El nombre no es decoración. "C6" no le dice nada a nadie en una mesa de ocho:
 * el mesero no tiene forma de saber cuál de las ocho personas es, así que
 * termina adivinando o anotando en papel. Con tres letras —"Sra", "Niño"— deja
 * de recordar y empieza a reconocer, y el cliente entiende su ticket.
 *
 * Quitar a alguien nunca borra lo que pidió: pasa a ser de la mesa. Una persona
 * que se va no puede llevarse los platillos que ya se sirvieron.
 */
export default function ComensalSheet({ open, seat, comensales = [], consumo = 0, onClose, onRename, onRemove }) {
  const [nombre, setNombre] = useState('');
  const { pedir, Confirmacion } = useConfirmacion();

  useEffect(() => {
    if (open) setNombre(comensales.find((c) => c.n === seat)?.nombre ?? '');
  }, [open, seat, comensales]);

  if (!open || !Number.isInteger(seat)) return null;

  const guardar = () => { onRename(seat, nombre); onClose(); };

  const quitar = async () => {
    const ok = await pedir({
      titulo: `¿Quitar a ${nombreDe(seat, comensales)} de la mesa?`,
      texto: consumo > 0 ? 'Tiene platillos apuntados: lo suyo pasa a ser de la mesa, no se pierde.' : 'Todavía no pide nada.',
      confirmar: 'Quitar',
      peligro: true,
    });
    if (!ok) return;
    onRemove(seat);
    onClose();
  };

  return (
    <div className={styles.scrim} role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.sheet} role="dialog" aria-modal="true">
        <div className={styles.head}>
          <div>
            <h2 className={styles.title}>Comensal {seat}</h2>
            <p className={styles.sub}>Ponle un nombre corto para reconocerlo</p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Cerrar"><IcoClose size={16} /></button>
        </div>

        {/* Los nombres que se usan en casi todas las mesas, a un toque: el
            mesero no teclea "Sra" con el cliente esperando. Tocar uno guarda. */}
        <div className={styles.rapidos}>
          {['Sra', 'Sr', 'Niño', 'Niña', 'Jefe', 'Invitado'].map((r) => (
            <button key={r} className={styles.rapido} onClick={() => { onRename(seat, r); onClose(); }}>{r}</button>
          ))}
        </div>

        <input
          className="input"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={`Sra, Niño, Jefe… (o déjalo en C${seat})`}
          maxLength={12}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && guardar()}
        />

        <button className={styles.opcionGhost} style={{ marginTop: 10 }} onClick={quitar}>
          <strong><IcoTrash size={13} /> Quitar de la mesa</strong>
          <small>{consumo > 0 ? 'Lo que pidió pasa a ser de la mesa' : 'Todavía no pide nada'}</small>
        </button>

        <div className={styles.acciones}>
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={guardar}>Guardar</button>
        </div>
      </div>
      {Confirmacion}
    </div>
  );
}
